import {
  Action,
  NgxsOnInit,
  Selector,
  State,
  StateContext,
  Store,
} from '@ngxs/store';
import { Injectable } from '@angular/core';
import { NotificationsService } from '@app/modules/notifications/service/notifications.service';
import { NotificationsActions } from '@app/modules/notifications/store/notifications.actions';
import { Observable } from 'rxjs';
import { catchError, filter, tap } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import {
  INotification,
  INotificationForDoctor,
  INotificationResponse,
} from '@app/modules/notifications/interface/notification';
import { CriticalInfoActions } from '@app/modules/critical-info/store/critical-info.actions';
import { LivingWillActions } from '@app/modules/living-will/store/living-will.actions';
import { InvitationsState } from '@app/modules/invitations/store/invitations.state';
import { NOTIFICATION_TYPE } from '../conts/notification';
import { UserState } from '@app/modules/user/store/user.state';
import { UserModel } from '@app/modules/user/model/user';
import { Navigate } from '@ngxs/router-plugin';
import { hasLiveSession } from '@app/modules/auth/utils/has-live-session';

export interface NotificationsStateModel {
  loading: boolean;
  items: INotification[];
  sidebarVisible: boolean;
  filter: string[];
  newNotificationCount: number;
  pagination: {
    currentPage: number;
    itemsPerPage: number;
    totalPages: number;
    totalItems: number;
  };
  countByType: {
    livingWillUpdated: number;
    generalInfoUpdated: number;
    criticalInfoUpdated: number;
    patientQrCodeScanned: number;
  };
}

@State<NotificationsStateModel>({
  name: 'notifications',
  defaults: {
    loading: false,
    items: [],
    sidebarVisible: false,
    filter: [],
    newNotificationCount: 0,
    pagination: {
      currentPage: 1,
      itemsPerPage: 20,
      totalPages: null,
      totalItems: null,
    },
    countByType: {
      livingWillUpdated: 0,
      generalInfoUpdated: 0,
      criticalInfoUpdated: 0,
      patientQrCodeScanned: 0,
    },
  },
})
@Injectable()
export class NotificationsState implements NgxsOnInit {
  @Selector()
  static loading(state: NotificationsStateModel): boolean {
    return state.loading;
  }

  @Selector()
  static sidebarVisible(state: NotificationsStateModel): boolean {
    return state.sidebarVisible;
  }

  @Selector()
  static currentPage(state: NotificationsStateModel): number {
    return state.pagination.currentPage;
  }

  @Selector()
  static totalPages(state: NotificationsStateModel): number {
    return state.pagination.totalPages;
  }

  @Selector([InvitationsState.newInvitationsCount])
  static newNotificationCount(
    state: NotificationsStateModel,
    invitationCount
  ): number {
    return state.newNotificationCount + invitationCount;
  }

  @Selector()
  static notificationCountByType(state: NotificationsStateModel): {
    livingWillUpdated: number;
    generalInfoUpdated: number;
    criticalInfoUpdated: number;
    patientQrCodeScanned: number;
  } {
    return state.countByType;
  }

  @Selector()
  static allNotifications(state: NotificationsStateModel): INotification[] {
    return state.items;
  }

  @Selector()
  static newCriticalNotifications(
    state: NotificationsStateModel
  ): INotificationForDoctor[] {
    const notificationForDoctor: INotificationForDoctor[] = [];
    const newCriticalNotifications = state.items.filter(
      (x) =>
        x.isViewed === false && x.type === NOTIFICATION_TYPE.CriticalInfoUpdated
    );
    if (newCriticalNotifications && newCriticalNotifications.length) {
      const uniquePatientIds = [
        ...new Set(newCriticalNotifications.map((x) => x.patient.id)),
      ];
      for (let id of uniquePatientIds) {
        const notificationsById = newCriticalNotifications.filter(
          (x) => x.patient.id === id
        );
        if (notificationsById && notificationsById.length) {
          const patientNotifications: INotificationForDoctor = {
            firstName: notificationsById[0].patient.firstName,
            lastName: notificationsById[0].patient.lastName,
            email: notificationsById[0].patient.email,
            items: notificationsById,
          };
          notificationForDoctor.push(patientNotifications);
        }
      }
    }
    return notificationForDoctor;
  }

  @Selector()
  static newNotifications(state: NotificationsStateModel): INotification[] {
    return state.items.filter(({ isViewed }) => !isViewed);
  }

  @Selector()
  static viewedNotifications(state: NotificationsStateModel): INotification[] {
    return state.items.filter(({ isViewed }) => isViewed);
  }

  /**
   * @param notificationsService
   */

  constructor(
    private readonly notificationsService: NotificationsService,
    private readonly store: Store
  ) {}

  ngxsOnInit(ctx: StateContext<NotificationsStateModel>): void {
    // Signed out (or a public break-glass page): fetch nothing. These
    // requests could only 401, and on the emergency card they compete with
    // the one request that matters.
    if (!hasLiveSession()) {
      return;
    }
    ctx.dispatch([
      new NotificationsActions.GetNotifications(),
      new NotificationsActions.CheckBlockers(),
    ]);
  }

  @Action(NotificationsActions.ChangeSidebarVisible)
  changeVisibilityPanel(
    ctx: StateContext<NotificationsStateModel>,
    { sidebarVisible }: NotificationsActions.ChangeSidebarVisible
  ): void {
    ctx.patchState({ sidebarVisible });

    if (!sidebarVisible) {
      ctx.patchState({ items: [] });
      ctx.dispatch(new NotificationsActions.GetNotifications(1, []));
    }
  }

  @Action([
    LivingWillActions.UpdateProxySuccess,
    LivingWillActions.UpdateWitnessSuccess,
    LivingWillActions.UpdateLivingWillSuccess,
    LivingWillActions.UpdateGeneralInfoSuccess,
    CriticalInfoActions.UpdateCriticalInfoSuccess,
  ])
  getLastUpdates(ctx: StateContext<NotificationsStateModel>): void {
    ctx.patchState({ items: [] });
    ctx.dispatch(new NotificationsActions.GetNotifications(1, []));
  }

  @Action([NotificationsActions.GetNotifications], { cancelUncompleted: true })
  getNotifications(
    ctx: StateContext<NotificationsStateModel>,
    {
      filter = ctx.getState().filter,
      page = ctx.getState().pagination.currentPage,
    }: NotificationsActions.GetNotifications
  ): Observable<void | INotificationResponse> {
    const { pagination } = ctx.getState();
    ctx.patchState({ loading: true });

    return this.notificationsService
      .getNotification(page, pagination.itemsPerPage, filter)
      .pipe(
        tap((response: INotificationResponse) => {
          const isTheSameFilter =
            filter.join() === ctx.getState().filter.join();
          ctx.patchState({
            filter,
            items: isTheSameFilter
              ? [...ctx.getState().items, ...response.items]
              : [...response.items],
            newNotificationCount: response.newNotificationCount,
            pagination: {
              ...ctx.getState().pagination,
              ...response.pagination,
            },

            countByType: response.countByType,
          });

          ctx.dispatch(new NotificationsActions.GetNotificationsSuccess());
        }),
        catchError((e: HttpErrorResponse) =>
          ctx.dispatch(new NotificationsActions.GetNotificationsFailed(e))
        )
      );
  }

  @Action(NotificationsActions.MarkAsRead, { cancelUncompleted: true })
  markAsRead(
    ctx: StateContext<NotificationsStateModel>,
    { notificationIds }
  ): Observable<void | boolean> {
    ctx.patchState({ loading: true });

    return this.notificationsService.markAsRead(notificationIds).pipe(
      tap(() => {
        ctx.patchState({ items: [] });
        ctx.dispatch(new NotificationsActions.GetNotifications(1));
        ctx.dispatch(new NotificationsActions.MarkAsReadSuccess());
      }),
      catchError((e: HttpErrorResponse) =>
        ctx.dispatch(new NotificationsActions.MarkAsReadFailed(e))
      )
    );
  }

  @Action(NotificationsActions.MarkAsReadAll, { cancelUncompleted: true })
  markAsReadAll(
    ctx: StateContext<NotificationsStateModel>
  ): Observable<void | boolean> {
    ctx.patchState({ loading: true });

    return this.notificationsService.markAsReadAll().pipe(
      tap(() => {
        ctx.patchState({ items: [] });
        ctx.dispatch(new NotificationsActions.GetNotifications(1));
        ctx.dispatch(new NotificationsActions.MarkAsReadAllSuccess());
      }),
      catchError((e: HttpErrorResponse) =>
        ctx.dispatch(new NotificationsActions.MarkAsReadAllFailed(e))
      )
    );
  }
  @Action(NotificationsActions.CheckBlockers, { cancelUncompleted: true })
  checkBlockers(ctx: StateContext<NotificationsStateModel>): void {
    // The doctor critical-change blocker is rendered INLINE by the doctor
    // dashboard now (app-doctor-blocker with its own *ngIf on the same
    // selector). The old behavior — an unscoped subscription navigating to
    // /office/dashboard/doctor-blocker — raced the login redirects: the
    // route component died mid-navigation and its body-appended dialog
    // survived as a dead, un-clickable overlay (user-reported 2026-08-14).
    // The route stays registered for direct visits; nothing navigates to it.
  }

  @Action([
    NotificationsActions.GetNotificationsSuccess,
    NotificationsActions.GetNotificationsFailed,
    NotificationsActions.MarkAsReadSuccess,
    NotificationsActions.MarkAsReadAllSuccess,
    NotificationsActions.MarkAsReadFailed,
    NotificationsActions.MarkAsReadAllFailed,
  ])
  stopLoading(ctx: StateContext<NotificationsStateModel>): void {
    ctx.patchState({ loading: false });
  }

  private get user(): UserModel {
    return this.store.selectSnapshot(UserState.model);
  }
}
